// convex/lib/config/domains/security.ts

import { validatorUsers } from "../../../system/users";
import { validatorRole } from "../../../system/roles";
import { validatorPermissions } from "../../../system/permissions";
import { validatorCompany } from "../../../companies";
import { permissionsRoleRule } from "../../../system/permissions/filter_rules";
import type { DomainGroup, TableScope } from "../types";

export const securityDomain: DomainGroup = {
  id: "security",
  name: "Seguridad y Accesos",
  description: "Gestión de usuarios, roles y permisos",
  icon: "Shield",
  order: 101,
  status: "active",
  modules: [
    {
      id: "users",
      name: "Usuarios",
      description: "Gestión de usuarios y permisos",
      icon: "Users",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/users", "/super/users"],
      navigation: {
        showInNav: true,
        contexts: ["settings", "super"],
        navOrder: 3,
        status: "active",
      },
      tables: [
        {
          tableName: "users",
          schema: validatorUsers,
          scope: "company" as TableScope,
          relations: [
            {
              key: "company",
              schema: validatorCompany,
              tableName: "companies",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "role",
              sourceField: "roleId",
              schema: validatorRole,
              tableName: "roles",
            },
          ],
          searchFields: ["name", "email", "phone"],
          hasStatusIndex: true,
        },
      ],
      readOnlyTables: [
        { tableName: "roles" },
        { tableName: "branches" },
        { tableName: "companies" },
        { tableName: "authAccounts" },
      ],
    },
    {
      id: "authAccounts",
      name: "Cuentas de Autenticación",
      description: "Gestión de cuentas de autenticación",
      icon: "KeyRound",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/security/auth-accounts"],
      navigation: {
        showInNav: false,
        contexts: ["none"],
        navOrder: 999,
        status: "active",
      },
      tables: [
        {
          // authAccounts no tenía config en tableConfigs — schema mínimo
          tableName: "authAccounts",
          schema: {},
          scope: "company" as TableScope,
        },
      ],
      readOnlyTables: [{ tableName: "users" }],
    },
    {
      id: "roles",
      name: "Roles",
      description: "Gestión de roles y permisos",
      icon: "ShieldCheck",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/roles", "/super/roles"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 2,
        status: "active",
      },
      tables: [
        {
          tableName: "roles",
          schema: validatorRole,
          scope: "company" as TableScope,
          relations: [
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["name", "description"],
          hasStatusIndex: false,
        },
      ],
      readOnlyTables: [{ tableName: "companies" }],
    },
    {
      id: "permissions",
      name: "Permisos",
      description: "Configuración de permisos",
      icon: "Lock",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/permissions", "/super/permissions"],
      navigation: {
        showInNav: false,
        contexts: ["super"],
        navOrder: 8,
        status: "active",
      },
      tables: [
        {
          tableName: "permissions",
          schema: validatorPermissions,
          scope: "company" as TableScope,
          relations: [
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "role",
              sourceField: "roleId",
              schema: validatorRole,
              tableName: "roles",
            },
          ],
          searchFields: ["module"],
          hasStatusIndex: false,
          customRules: [permissionsRoleRule],
        },
      ],
      readOnlyTables: [{ tableName: "roles" }],
    },
  ],
};
